import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly label: string;
  readonly amount: number;
  readonly currencyCode: CurrencyCodeType;
  readonly isLoading?: boolean;
  readonly tooltip?: string;
  readonly subtitle?: string;
}

export const AmountCard: React.FC<Props> = ({
  label,
  amount,
  currencyCode,
  isLoading = false,
  tooltip,
  subtitle,
}: Props): React.ReactElement => {
  const tr = useTranslator();
  const formatter = useCurrencyFormatter();

  const formattedAmount = React.useMemo(
    (): string => formatter.format(amount, currencyCode),
    [amount, currencyCode, formatter],
  );

  return (
    <Box sx={styles.card}>
      <Stack sx={styles.cardContent}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography sx={styles.cardLabel}>{tr(label)}</Typography>
          {tooltip && (
            <Tooltip title={tr(tooltip)} placement="top">
              <Box sx={styles.tooltipIcon}>
                <InfoIcon fill="currentColor" width={16} height={16} />
              </Box>
            </Tooltip>
          )}
        </Stack>
        {isLoading ? (
          <Skeleton variant="rounded" width={200} height={36} />
        ) : (
          <Typography sx={styles.amount}>{formattedAmount}</Typography>
        )}
        {subtitle && <Typography sx={styles.subtitle}>{subtitle}</Typography>}
      </Stack>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  card: {
    flex: 1,
    bgcolor: 'custom.Primary1Background',
    borderRadius: 2,
    padding: 3,
  },
  cardContent: {
    gap: 0.5,
  },
  cardLabel: {
    color: 'custom.LightGray',
  },
  amount: {
    fontSize: 24,
    fontWeight: 400,
    lineHeight: '120%',
    letterSpacing: '-1%',
  },
  tooltipIcon: {
    display: 'flex',
    alignItems: 'center',
    color: 'custom.LightGray',
    cursor: 'pointer',
  },
  subtitle: {
    fontSize: 12,
    color: 'custom.LightGray',
  },
};
